"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, TrendingUp, Search, Users, Target, Compass, ArrowRight, CircleCheck as CheckCircle2, Sparkles, ChartBar as BarChart3 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const marketMetrics = [{
  id: "metric-1",
  stat: "78%",
  label: "Local Search Intent",
  description: "of Carlisle consumers search online before visiting a local Cumbrian business.",
  badgeText: "High Intent"
}, {
  id: "metric-2",
  stat: "3.4x",
  label: "Revenue Potential",
  description: "higher conversion rate for local businesses appearing in the top 3 Google Map Pack.",
  badgeText: "Map Pack Advantage"
}, {
  id: "metric-3",
  stat: "64%",
  label: "Unclaimed Visibility",
  description: "of local Carlisle services lack a structured technical SEO & keyword strategy.",
  badgeText: "Market Gap"
}];
const searchBehaviors = [{
  id: "behavior-1",
  title: "Geotargeted 'Near Me' Searches",
  description: "Carlisle buyers rely heavily on localized queries such as 'best SEO consultant in Carlisle' or 'Cumbria legal services'. Capturing local intent requires precision structured schema and localized content.",
  highlight: "82% mobile driven search traffic"
}, {
  id: "behavior-2",
  title: "Map Pack & Micro-Location Focus",
  description: "Whether targeting the city centre, Kingstown Industrial Estate, or surrounding Cumbria regions, local map pack positioning dictates who gets the first inquiry.",
  highlight: "Direct map-to-call conversion"
}, {
  id: "behavior-3",
  title: "High Commercial Buyer Intent",
  description: "B2B and high-ticket service queries in Carlisle carry high intent. Searchers compare expertise, clear deliverables, and genuine consultant authority over generic agency fluff.",
  highlight: "Shorter sales cycles with SEO"
}, {
  id: "behavior-4",
  title: "AI & Zero-Click Overview Shifts",
  description: "Modern searchers use Google Gemini and AI Overviews to answer regional queries. I optimize your brand so AI engines explicitly recommend your specific services in Carlisle.",
  highlight: "Future-proof Search Visibility"
}];
const opportunityPoints = ["Outrank national directories crowding Carlisle search results", "Capture high-margin local customer queries with dedicated landing pages", "Build authoritative local citations & trusted Cumbrian backlinks", "Transparent, direct consultant access — no junior account managers"];
export default function CarlisleLandscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="carlislemarketlandscape" data-nav="light" className="relative w-full bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      <div className="absolute top-0 right-0 -z-10 w-96 h-96 bg-secondary/40 rounded-full blur-3xl opacity-50 pointer-events-none" />
      <div className="absolute bottom-0 left-0 -z-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl opacity-40 pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : -20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="flex flex-col items-center text-center max-w-3xl mx-auto space-y-4 mb-16">
          <Badge variant="secondary" className="px-3 py-1 text-sm font-medium rounded-full bg-secondary text-secondary-foreground border border-accent/20 flex items-center gap-1.5">
            <MapPin className="size-3.5 text-primary" />
            <span>Carlisle & Cumbria Search Intelligence</span>
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-primary font-default">
            Navigating the Carlisle Search Landscape
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            Understanding local customer search behavior in Carlisle is key to dominating search rankings. I analyze real search patterns across Cumbria to help your business capture high-intent organic traffic directly.
          </p>
        </motion.div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {marketMetrics.map((metric, i) => <motion.div key={metric.id} variants={itemVariants} data-index={i}>
              <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-300 hover:border-primary/30 relative overflow-hidden group">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-accent" />
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant="outline" className="bg-secondary/50 text-secondary-foreground border-accent/30 font-medium">
                      {metric.badgeText}
                    </Badge>
                    <BarChart3 className="size-5 text-primary opacity-80" />
                  </div>
                  <div className="text-4xl lg:text-5xl font-extrabold text-primary tracking-tight font-default">
                    {metric.stat}
                  </div>
                  <CardTitle className="text-lg font-semibold text-foreground mt-2">
                    {metric.label}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                    {metric.description}
                  </CardDescription>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-16">
          <motion.div initial={{
          opacity: 0,
          x: shouldReduceMotion ? 0 : -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="lg:col-span-7 space-y-6">
            <div className="space-y-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-accent font-default">
                Consumer Dynamics
              </span>
              <h3 className="text-2xl sm:text-3xl font-bold text-primary font-default">
                How Carlisle Customers Search & Convert
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base">
                Local searchers in Carlisle don&apos;t just browse — they seek targeted local solutions. When you work directly with me, we align your digital strategy with their real habits.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              {searchBehaviors.map((item, i) => <div key={item.id} data-index={i} className="p-4 rounded-xl bg-card border border-border shadow-xs space-y-2 hover:border-primary/20 transition-colors">
                  <div className="flex items-center gap-2">
                    <Compass className="size-4 text-primary shrink-0" />
                    <h4 className="font-semibold text-sm text-foreground font-default">
                      {item.title}
                    </h4>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {item.description}
                  </p>
                  <div className="pt-1">
                    <span className="inline-block px-2 py-0.5 text-[13px] font-medium bg-secondary text-secondary-foreground rounded border border-accent/20">
                      {item.highlight}
                    </span>
                  </div>
                </div>)}
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: shouldReduceMotion ? 0 : 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="lg:col-span-5">
            <Card className="border border-border bg-card shadow-md relative overflow-hidden rounded-2xl p-2">
              <div className="bg-muted/50 rounded-xl p-6 sm:p-8 space-y-6 border border-border/50">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-primary">
                    <Sparkles className="size-5" />
                    <span className="font-semibold text-sm uppercase tracking-wide">
                      Strategic Advantage
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold text-foreground font-default">
                    The Carlisle Market Opportunity
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Many regional competitors rely on outdated agency tactics. As an independent SEO consultant, I deliver agile, bespoke optimization tailored specifically to Cumbria business growth.
                  </p>
                </div>

                <Separator className="bg-border" />

                <div className="space-y-3">
                  {opportunityPoints.map((point, index) => <div key={index} data-index={index} className="flex items-start gap-3">
                      <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                      <span className="text-sm font-medium text-foreground leading-snug">
                        {point}
                      </span>
                    </div>)}
                </div>

                <div className="pt-2">
                  <Link to="/ContactPage" className="block w-full">
                    <Button size="lg" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-md font-medium group">
                      <span>Discuss Your Carlisle SEO Growth</span>
                      <ArrowRight className="size-4 ml-1 transition-transform group-hover:translate-x-1" />
                    </Button>
                  </Link>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="rounded-2xl bg-secondary/40 border border-accent/20 p-6 sm:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-2 text-center md:text-left max-w-2xl">
            <h3 className="text-xl sm:text-2xl font-bold text-primary font-default">
              Want to capture more search share in Carlisle?
            </h3>
            <p className="text-sm sm:text-base text-foreground/80">
              You work directly with me — Yassine Essaadi. No account managers, no generic templates, just tailored SEO strategy designed for Carlisle businesses.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0">
            <Link to="/ContactPage">
              <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm font-medium">
                Book a Free SEO Consultation
              </Button>
            </Link>
            <Link to="/CaseStudiesPage">
              <Button size="lg" variant="outline" className="border-border text-foreground hover:bg-background/80">
                Explore Case Studies
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>;
}